import { IMove, Position, GameState, MoveType, EffectType } from "../types";
import { ZodiacEngine } from "../zodiacEngine";
import { HeuristicInterface } from "./HeuristicInterface";
import { Piece } from "../Piece";

/**
 * Plain heuristic implementation
 * This implements two simple rules:
 * 1. For every position it moves forward from the starting position, the eval changes:
 *    - Dark pieces advancing from row 0 increases eval
 *    - Light pieces advancing from row 7 decreases eval
 * 2. For every piece on the board, +10 points for own pieces, -10 points for enemy pieces
 */
export class PlainHeuristic implements HeuristicInterface {
  name = "Plain";
  private debugging: boolean = false;

  constructor() {
    // Ensure debugging is off by default
    this.debugging = false;
  }

  /**
   * Enable or disable debug logging
   * @param debug Whether to enable debug logging
   */
  setDebugging(debug: boolean): void {
    this.debugging = debug;
  }

  /**
   * Policy filter for Plain heuristic
   * We only do basic filtering of problematic move types
   *
   * @param engine The ZodiacEngine instance
   * @param gameState Current game state
   * @param fromPosition Position of the piece to move
   * @param legalMoves Array of legal moves to filter
   * @returns Filtered array of legal moves that pass the policy
   */
  policyFilter(
    engine: ZodiacEngine | undefined,
    gameState: GameState,
    fromPosition: Position,
    legalMoves: IMove[]
  ): IMove[] {
    // Filter out problematic move types
    return legalMoves.filter((move) => {
      // Remove the filter that excluded rotate moves
      // Skip goat faint moves for the AI (dark player)
      if (move.moveType === MoveType.faint && !gameState.isLightTurn) {
        return false;
      }
      return true;
    });
  }

  /**
   * Evaluates a static position for use at MCTS leaf nodes
   *
   * @param gameState The game state to evaluate
   * @returns A score representing the position's value from dark player's perspective
   */
  evaluatePosition(gameState: GameState): number {
    // Terminal game over check
    if (gameState.lightWon === true) return -1000000; // Dark lost
    if (gameState.lightWon === false) return 1000000; // Dark won

    // Initialize score
    let score = 0;

    // Rule 1: Development evaluation - assess how far pieces have advanced
    score += this.evaluateDevelopment(gameState);

    // Rule 2: Material evaluation - count all pieces on the board
    score += this.evaluateMaterial(gameState);

    // Bonus: Give 1000 points if dark king reaches row 0
    score += this.checkKingReachedGoal(gameState);

    // New: Bonus if dark king is one move away from goal (row 1, and row 0 is open or capturable)
    for (let col = 0; col < gameState.board[0].length; col++) {
      const square = gameState.board[1][col];
      if (square.Pieces.length > 0) {
        const piece = square.Pieces[0];
        if (!piece.isLight && piece.isKing) {
          // Check if row 0, same col, is empty or contains a non-light piece
          const goalSquare = gameState.board[0][col];
          if (
            goalSquare.Pieces.length === 0 ||
            goalSquare.Pieces[0].isLight === false
          ) {
            score += 500; // Large bonus for being one move from goal
          }
        }
      }
    }

    // King race bonus/penalty (bootleg, for plain heuristic)
    // Find kings
    let lightKingPos: { row: number; col: number } | null = null;
    let darkKingPos: { row: number; col: number } | null = null;
    const boardRows = gameState.board.length;
    const midpoint = Math.floor(boardRows / 2);
    const ADVANCE_BONUS = 10000;
    const ADVANCE_PENALTY = 10000;
    for (let row = 0; row < boardRows; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          if (piece.isKing) {
            if (piece.isLight) lightKingPos = { row, col };
            else darkKingPos = { row, col };
          }
        }
      }
    }
    if (darkKingPos && darkKingPos.row < midpoint) {
      score += (midpoint - darkKingPos.row) * ADVANCE_BONUS;
    }
    if (lightKingPos && lightKingPos.row > midpoint) {
      score -= (lightKingPos.row - midpoint) * ADVANCE_PENALTY;
    }

    if (this.debugging) {
    }

    return score;
  }

  /**
   * Evaluates a move according to the Plain heuristic's simple rules
   *
   * @param engine The ZodiacEngine instance
   * @param gameState Current game state
   * @param fromPosition Position of the piece to move
   * @param move The potential move to evaluate
   * @returns Score based on the move's forward progress and material changes
   */
  evaluateMove(
    engine: ZodiacEngine,
    gameState: GameState,
    fromPosition: Position,
    move: IMove
  ): number {
    const isLight = gameState.isLightTurn;

    // Get the moving piece
    const movingPiece = this.getPieceAt(gameState, fromPosition);
    const pieceType = movingPiece ? movingPiece.type : "Unknown";

    // Create a cloned state that simulates the move
    const clonedState = this.simulateMove(gameState, fromPosition, move);

    // Evaluate the resulting position
    const score = this.evaluatePosition(clonedState);

    if (this.debugging) {
      // Special logging for penguin rotations
      if (
        move.moveType === MoveType.noMove &&
        move.sideEffects?.some((se) => se.effect.type === EffectType.rotate)
      ) {
        const rotateDirection = (
          move.sideEffects.find(
            (se) => se.effect.type === EffectType.rotate
          ) as any
        )?.rotateDirection;
      } else {
      }
    }

    return score;
  }

  /**
   * Evaluates the development of both sides on the board
   * @param gameState The game state to evaluate
   * @returns A score representing the development advantage from dark player's perspective
   */
  private evaluateDevelopment(gameState: GameState): number {
    let developmentScore = 0;
    let debugInfo = [];

    // Go through the board and evaluate all pieces
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          let pieceAdvancement = 0;

          if (piece.isLight) {
            // Light pieces start at row 0
            // Calculate advancement row by row for proper doubling at rows 0-1
            let advancement = 0;

            // Go through each row the piece has advanced through
            for (let r = 0; r < row; r++) {
              // If it's one of the first two rows (0-1), add 2 points
              if (r <= 1) {
                advancement += 2;
              } else {
                // Otherwise add 1 point per row
                advancement += 1;
              }
            }

            pieceAdvancement = -advancement;
            developmentScore += pieceAdvancement;
          } else {
            // Dark pieces start at row 7
            // Calculate advancement row by row for proper doubling at rows 6-7
            let advancement = 0;

            // Go through each row the piece has advanced through
            for (let r = 7; r > row; r--) {
              // If it's one of the last two rows (6-7), add 2 points
              if (r >= 6) {
                advancement += 2;
              } else {
                // Otherwise add 1 point per row
                advancement += 1;
              }
            }

            pieceAdvancement = advancement;
            developmentScore += pieceAdvancement;
          }

          // Store debug info but don't display it
          if (this.debugging) {
            debugInfo.push({
              piece: piece.type,
              row,
              col,
              isLight: piece.isLight,
              advancement: pieceAdvancement,
            });
          }
        }
      }
    }

    // Print development details only when debugging is enabled
    if (this.debugging) {
      debugInfo.forEach((info) => {
      });
    }

    return developmentScore;
  }

  /**
   * Evaluates the material balance on the board
   * @param gameState The game state to evaluate
   * @returns A score representing the material advantage from dark player's perspective
   */
  private evaluateMaterial(gameState: GameState): number {
    let darkPieceCount = 0;
    let lightPieceCount = 0;
    let darkKingCount = 0;
    let darkFrogWithLilypadCount = 0;
    let darkFrogWithoutLilypadCount = 0;
    let lightFrogWithLilypadCount = 0;
    let lightFrogWithoutLilypadCount = 0;
    let darkSnakeNotShedCount = 0;
    let darkSnakeShedCount = 0;
    let lightSnakeNotShedCount = 0;
    let lightSnakeShedCount = 0;

    // Count all pieces on the board
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces.length > 0) {
          const piece = square.Pieces[0];

          if (piece.isLight) {
            // Check if it's a frog with or without lilypad
            if (piece.type === "Frog") {
              if (piece.hasLilyJumped) {
                lightFrogWithoutLilypadCount++;
              } else {
                lightFrogWithLilypadCount++;
              }
            }
            // Check if it's a snake with or without shed
            else if (piece.type === "Snake") {
              if (piece.hasShed) {
                lightSnakeShedCount++;
              } else {
                lightSnakeNotShedCount++;
              }
            } else {
              lightPieceCount++;
            }
          } else {
            // Check if it's a dark king
            if (piece.isKing) {
              darkKingCount++;
            }
            // Check if it's a frog with or without lilypad
            else if (piece.type === "Frog") {
              if (piece.hasLilyJumped) {
                darkFrogWithoutLilypadCount++;
              } else {
                darkFrogWithLilypadCount++;
              }
            }
            // Check if it's a snake with or without shed
            else if (piece.type === "Snake") {
              if (piece.hasShed) {
                darkSnakeShedCount++;
              } else {
                darkSnakeNotShedCount++;
              }
            } else {
              darkPieceCount++;
            }
          }
        }
      }
    }

    // Increase material value to make captures much more important
    // This significantly increases the value of material advantage
    const regularPieceValue = 100; // Doubled from 10

    // Calculate material advantage with increased values:
    // - Dark king is now worth 40 points (doubled)
    // - All other values are similarly doubled to make material more important
    return (
      darkPieceCount * regularPieceValue +
      darkKingCount * 150 +
      darkFrogWithLilypadCount * 130 +
      darkFrogWithoutLilypadCount * 90 +
      darkSnakeNotShedCount * 90 +
      darkSnakeShedCount * 100 -
      lightPieceCount * regularPieceValue -
      lightFrogWithLilypadCount * 130 -
      lightFrogWithoutLilypadCount * 90 -
      lightSnakeNotShedCount * 90 -
      lightSnakeShedCount * 100
    );
  }

  /**
   * Creates a simulated game state after making a move
   * @param gameState Current game state
   * @param fromPosition Position of the piece to move
   * @param move The move to make
   * @returns A new game state with the move applied
   */
  public simulateMove(
    gameState: GameState,
    fromPosition: Position,
    move: IMove
  ): GameState {
    // Create a deep clone of the game state
    const clonedState = JSON.parse(JSON.stringify(gameState));

    // CRITICAL FIX: Reconstruct piece objects with their prototype methods
    // After JSON cloning, pieces lose their getValidMovesForPiece method
    for (let row = 0; row < clonedState.board.length; row++) {
      for (let col = 0; col < clonedState.board[row].length; col++) {
        const square = clonedState.board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          for (let i = 0; i < square.Pieces.length; i++) {
            const pieceData = square.Pieces[i];
            // Create new Piece object with proper prototype
            const reconstructedPiece = new Piece(
              pieceData.type,
              pieceData.isLight,
              pieceData.direction
            );

            // CRITICAL FIX: Store the properly initialized movementStrategy
            const properMovementStrategy = reconstructedPiece.movementStrategy;

            // Copy all properties from the cloned data
            Object.assign(reconstructedPiece, pieceData);

            // CRITICAL FIX: Restore the proper movementStrategy (don't let Object.assign overwrite it)
            reconstructedPiece.movementStrategy = properMovementStrategy;

            // Replace the plain object with the proper Piece instance
            square.Pieces[i] = reconstructedPiece;
          }
        }
      }
    }

    // Get the piece to be moved
    const movingPiece = this.getPieceAt(clonedState, fromPosition);
    if (!movingPiece) return clonedState;

    // Remove the piece from its current position
    const fromSquare = clonedState.board[fromPosition.row][fromPosition.col];
    fromSquare.Pieces = fromSquare.Pieces.filter(
      (p: any) => p.id !== movingPiece.id
    );

    // Place the piece at its new position
    const toSquare = clonedState.board[move.position.row][move.position.col];

    // Handle captures (remove captured piece)
    if (move.toPiece) {
      toSquare.Pieces = toSquare.Pieces.filter(
        (p: any) => p.id !== move.toPiece!.id
      );
    }

    // Add the moving piece to the destination
    toSquare.Pieces.push(movingPiece);

    // CRITICAL FIX: Handle side effects, especially entangle and rotate
    if (move.sideEffects && move.sideEffects.length > 0) {
      for (const sideEffect of move.sideEffects) {
        if (sideEffect.effect.type === EffectType.entangle) {
          // Handle entangle side effects
          if (
            sideEffect.piecesToEffect &&
            sideEffect.piecesToEffect.length > 0
          ) {
            for (const targetPiece of sideEffect.piecesToEffect) {
              // Find the target piece in the cloned state and entangle it
              for (let row = 0; row < clonedState.board.length; row++) {
                for (let col = 0; col < clonedState.board[row].length; col++) {
                  for (const piece of clonedState.board[row][col].Pieces) {
                    if (piece.id === targetPiece.id) {
                      piece.isEntangled = true;
                      break;
                    }
                  }
                }
              }
            }
          }
        } else if (sideEffect.effect.type === EffectType.rotate) {
          // Handle rotation side effects (for penguin rotations)
          const rotateDirection = (sideEffect as any).rotateDirection;
          if (rotateDirection && movingPiece) {
            movingPiece.direction = rotateDirection;
            if (this.debugging) {
            }
          }
        }
      }
    }

    return clonedState;
  }

  /**
   * Helper method to get a piece at a specific position
   * @param gameState Current game state
   * @param position Position to check
   * @returns The piece at the position or null if none
   */
  private getPieceAt(gameState: GameState, position: Position): any {
    if (
      position.row < 0 ||
      position.row >= gameState.board.length ||
      position.col < 0 ||
      position.col >= gameState.board[0].length
    ) {
      return null;
    }

    const square = gameState.board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0]; // Return the top piece
  }

  /**
   * Checks if the dark king has reached row 0 (the goal)
   * @param gameState The game state to evaluate
   * @returns 1000 if dark king is at row 0, otherwise 0
   */
  private checkKingReachedGoal(gameState: GameState): number {
    // Check row 0 for dark king
    for (let col = 0; col < gameState.board[0].length; col++) {
      const square = gameState.board[0][col];
      if (square.Pieces.length > 0) {
        const piece = square.Pieces[0];
        if (!piece.isLight && piece.isKing) {
          // Dark king reached row 0
          return 1000;
        }
      }
    }
    return 0;
  }
}
